import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useWidth } from '../../../hooks/useWidth';
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { BA_TIMEZONE, formatTimezoneName } from '../../../utils/timezone';

type MobileDayProps = {
  label: string;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
};

type Props = {
  startOfWeek: Date;
  canGoPrev: boolean;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  timezone?: string;
  editMode?: boolean;
  showDate?: boolean;
  mobileDay?: MobileDayProps;
  viewSelector?: React.ReactNode;
};

const CalendarHeader: React.FC<Props> = ({ startOfWeek, canGoPrev, canGoNext, onPrev, onNext, onToday, timezone, editMode = false, showDate = false, mobileDay, viewSelector }) => {
  const { size } = useWidth();
  const navigate = useNavigate();
  const monthName = startOfWeek.toLocaleDateString('es-ES', { month: 'long' });
  const monthLabel = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${startOfWeek.getFullYear()}`;
  
  const detectedTimezone = timezone || BA_TIMEZONE;
  const gmtOffset = new Date().toLocaleTimeString('en-US', { timeZone: detectedTimezone, timeZoneName: 'shortOffset' }).match(/GMT[+-]\d+/)?.[0] || '';
  const headerIso2 = timezoneToCountryCode(detectedTimezone);
  const timezoneLabel = `${gmtOffset} - ${formatTimezoneName(detectedTimezone)}`;
  
  return (
    <div className="calendar-header" style={editMode && !showDate ? { marginLeft: 'auto', width: `clamp(min(47.3rem, 100%), -30.174rem + 100.632vw, 72rem)` } : undefined}>
      <div className="header-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.375rem' }}>
        {!editMode && size !== 's' && (
          <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button className="btn-outline" onClick={onToday}>Hoy</button>
              {viewSelector}
            </div>
          </>
        )}
      </div>
      <div className="header-center">
        {editMode && !showDate ? (
          <div style={{ margin: '0 auto', paddingLeft: '0.5rem', textAlign: 'center', overflow: 'hidden' }}>

          </div>
        ) : mobileDay ? (
          <div id="calendar-week-arrows" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              aria-label="previous-day"
              onClick={mobileDay.onPrev}
              disabled={!mobileDay.canPrev}
              style={{
                background: 'transparent',
                border: 0,
                color: '#6D57BC',
                fontSize: '3.5rem',
                width: '4rem',
                height: '4rem',
                display: 'grid',
                placeItems: 'center',
                cursor: mobileDay.canPrev ? 'pointer' : 'not-allowed',
                opacity: mobileDay.canPrev ? 1 : 0.35,
                alignContent: 'center'
              }}
            >
              ‹
            </button>
            <h3 style={{ margin: 0, color: '#6D57BC', fontWeight: 700, whiteSpace: 'nowrap' }}>{mobileDay.label}</h3>
            <button
              aria-label="next-day"
              onClick={mobileDay.onNext}
              disabled={!mobileDay.canNext}
              style={{
                background: 'transparent',
                border: 0,
                color: '#6D57BC',
                fontSize: '3.5rem',
                width: '4rem',
                height: '4rem',
                display: 'grid',
                placeItems: 'center',
                cursor: mobileDay.canNext ? 'pointer' : 'not-allowed',
                opacity: mobileDay.canNext ? 1 : 0.35,
                alignContent: 'center'
              }}
            >
              ›
            </button>
          </div>
        ) : (
          <div id="calendar-week-arrows" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              id="calendar-prev-week"
              aria-label="previous-week"
              onClick={onPrev}
              disabled={!canGoPrev}
              style={{
                background: 'transparent',
                border: 0,
                color: '#6D57BC',
                fontSize: '3.5rem',
                width: '4rem',
                height: '4rem',
                display: 'grid',
                placeItems: 'center',
                cursor: canGoPrev ? 'pointer' : 'not-allowed',
                opacity: canGoPrev ? 1 : 0.35,
                alignContent: 'center'
              }}
            >
              ‹
            </button>
            <h3 style={{ margin: 0, color: '#6D57BC', fontWeight: 700, whiteSpace: 'nowrap' }}>{monthLabel}</h3>
            <button
              id="calendar-next-week"
              aria-label="next-week"
              onClick={onNext}
              disabled={!canGoNext}
              style={{
                background: 'transparent',
                border: 0,
                color: '#6D57BC',
                fontSize: '3.5rem',
                width: '4rem',
                height: '4rem',
                display: 'grid',
                placeItems: 'center',
                cursor: canGoNext ? 'pointer' : 'not-allowed',
                opacity: canGoNext ? 1 : 0.35,
                alignContent: 'center'
              }}
            >
              ›
            </button>
          </div>
        )}
      </div>
      <div className="header-right flex items-center gap-1.5">
        {size !== 's' && !editMode && (
          <>
            <CountryFlag iso2={headerIso2} size="0.9rem" />
            <select
              disabled
              value={detectedTimezone}
              style={{
                  padding: '0.375rem 0.625rem',
                  fontSize: '0.8125rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.25rem',
                  background: '#f9fafb',
                  color: '#6b7280',
                  cursor: 'not-allowed',
                  }}
            >
              <option value={detectedTimezone}>{timezoneLabel}</option>
            </select>
            <button
              type="button"
              aria-label="Editar huso horario"
              title="Editar huso horario"
              onClick={() => navigate('/profile', { state: { openTimezoneModal: true } })}
              className="grid place-items-center p-1.5 rounded-md border-0 bg-transparent text-[#6D57BC] hover:bg-violet-50 cursor-pointer"
            >
              <Pencil size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarHeader;


